import React, { useState } from 'react'
import { Layout, Drawer, Grid } from 'antd'
import { observer } from 'mobx-react-lite'
import type { UIManager } from '@/Core/Managers/UI'

import { HeaderContent } from '../HeaderContent/HeaderContent'
import { Menu } from '../Menu/Menu'
import { UserProfileBlock } from '../UserProfileBlock/UserProfileBlock'
import { SidebarToggle } from './SidebarToggle'
import { AppLogo } from '@/Ship/Components/UI/AppLogo/AppLogo'
import { sidebarStore } from '@/Ship/Stores/SidebarStore'
import { themeStore } from '@/Ship/Stores/ThemeStore'

import styles from './DefaultLayout.module.css'

const { Sider, Content } = Layout
const { useBreakpoint } = Grid

interface DefaultLayoutProps {
    layoutContext?: UIManager
    children: React.ReactNode
}

// layoutContext лишається в пропсах для сумісності з шаблоном, тут не використовується
export const DefaultLayout = observer(({ children }: DefaultLayoutProps) => {
    // Ручне згортання зберігається в localStorage (SidebarStore), як і тема
    const collapsed = sidebarStore.collapsed
    const [mobileOpen, setMobileOpen] = useState(false)
    const screens = useBreakpoint()

    // md < 768px = mobile, md..lg = tablet (992px), lg+ = desktop
    const isMobile = !screens.md
    const isTablet = !!screens.md && !screens.lg
    // У мобільному Drawer сайдбар завжди розгорнутий; на планшеті завжди згорнутий
    const isCollapsed = isMobile ? false : isTablet ? true : collapsed
    const canToggle = !isMobile && !isTablet

    const sidebarContent = (
        <>
            <div className={`${styles['logoRow']}${isCollapsed ? ` ${styles['logoRowCollapsed']}` : ''}`}>
                <AppLogo collapsed={isCollapsed} tone={themeStore.mode === 'light' ? 'dark' : 'light'} />
                {canToggle && <SidebarToggle collapsed={collapsed} onClick={() => sidebarStore.toggle()} />}
            </div>
            <div className={styles['menuWrapper']}>
                <Menu />
            </div>
            <UserProfileBlock collapsed={isCollapsed} />
        </>
    )

    const contentClass = isMobile
        ? styles['contentMobile']
        : isTablet || collapsed
          ? `${styles['content']} ${styles['contentCollapsed']}`
          : styles['content']

    // Тему antd задає Core Layout через AntdConfig (геттер theme + observer Root у main.tsx),
    // тому тут окремий ConfigProvider не потрібен: він не накрив би Core-дровери поза DefaultLayout.
    return (
        <>
            <Layout className={styles['layout']}>
                {isMobile ? (
                    <Drawer
                        open={mobileOpen}
                        onClose={() => setMobileOpen(false)}
                        placement="left"
                        width={260}
                        closable={false}
                        title={null}
                        className={styles['mobileDrawer'] ?? ''}
                        styles={{
                            body: {
                                padding: 0,
                                background: 'var(--as-sidebar-bg)',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                overflowX: 'hidden',
                            },
                        }}
                    >
                        {sidebarContent}
                    </Drawer>
                ) : (
                    <Sider
                        className={styles['sider'] ?? ''}
                        trigger={null}
                        collapsible
                        collapsed={isCollapsed}
                        width={260}
                        collapsedWidth={80}
                    >
                        {sidebarContent}
                    </Sider>
                )}

                <Layout className={contentClass}>
                    <div className={styles['header']} data-layout-header>
                        <HeaderContent {...(isMobile ? { onMenuToggle: () => setMobileOpen(true) } : {})} />
                    </div>
                    <Content className={styles['main']}>{children}</Content>
                </Layout>
            </Layout>
        </>
    )
})
